import React from 'react';
import styled from 'styled-components';

import { Button } from 'shared/components';
import { ReorderUnavailableItem, UnavailableReason } from 'src/hooks/use-reorder-items';

type ReorderUnavailableItemsProps = {
  items: ReorderUnavailableItem[];
  onClose: () => void;
};

enum AvailabilityBadge {
  outOfStock = 'out_of_stock',
  lowerQty = 'lower_qty',
  overLimit = 'over_limit',
}

type AmountAvailableDisplay = { text: string; badge: AvailabilityBadge };

function getAmountAvailableContent(item: ReorderUnavailableItem): AmountAvailableDisplay {
  if (item.reason === UnavailableReason.overLimit) {
    return { text: 'Exceeds limit', badge: AvailabilityBadge.overLimit };
  }
  if (item.reason === UnavailableReason.notFound) {
    return { text: 'Out of stock', badge: AvailabilityBadge.outOfStock };
  }
  if (item.availableQuantity && item.availableQuantity > 0) {
    return { text: String(item.availableQuantity), badge: AvailabilityBadge.lowerQty };
  }
  return { text: 'Out of stock', badge: AvailabilityBadge.outOfStock };
}

export const ReorderUnavailableItems = ({ items, onClose }: ReorderUnavailableItemsProps): JSX.Element => (
  <Container>
    <Header>Item(s) Unavailable</Header>
    <Description>Some items from your past order are currently unavailable.</Description>
    <Divider />
    <ItemsList>
      {items.map((item) => {
        const amountAvailable = getAmountAvailableContent(item);
        const isOverLimit = item.reason === UnavailableReason.overLimit;
        return (
          <ItemRow key={`${item.name}-${item.option ?? ''}-${item.requestedQuantity}`}>
            <ItemHeader>
              {item.image && <ItemImage src={item.image} alt={item.name} />}
              <ItemName>
                {item.name}
                {item.option ? ` (${item.option})` : ''}
              </ItemName>
            </ItemHeader>
            <DetailRow>
              <DetailLabel>Previously bought:</DetailLabel>
              <DetailValue>{item.requestedQuantity}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{isOverLimit ? 'Status:' : 'Amount available:'}</DetailLabel>
              <DetailValue>
                {amountAvailable.badge === AvailabilityBadge.outOfStock ? (
                  <OutOfStockBadge>{amountAvailable.text}</OutOfStockBadge>
                ) : (
                  <WarningBadge>{amountAvailable.text}</WarningBadge>
                )}
              </DetailValue>
            </DetailRow>
            {item.limitMessage && (
              <DetailRow>
                <DetailLabel>Reason:</DetailLabel>
                <DetailValue>{item.limitMessage}</DetailValue>
              </DetailRow>
            )}
          </ItemRow>
        );
      })}
    </ItemsList>
    <UpdateCartButton onClick={onClose}>UPDATE CART & REVIEW</UpdateCartButton>
  </Container>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: left;
  max-height: 70vh;
`;

const Header = styled.h2`
  font-size: 20px;
  font-weight: bold;
`;

const Description = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.grey[30]};
  margin-top: -8px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  margin: 0;
`;

const ItemsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ItemRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:not(:first-child) {
    border-top: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
    padding-top: 20px;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const ItemImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
`;

const ItemName = styled.span`
  font-size: 16px;
  font-weight: bold;
  line-height: 20px;
`;

const DetailRow = styled.div`
  display: flex;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const DetailLabel = styled.span`
  width: 164px;
  flex-shrink: 0;
`;

const DetailValue = styled.span``;

const OutOfStockBadge = styled.span`
  background: #fae8ea;
  color: #920715;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
`;

const WarningBadge = styled.span`
  background: #fdf4e0;
  color: #7a5c1f;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
`;

const UpdateCartButton = styled(Button)`
  width: 100%;
  border-radius: 9999px;
`;
